import { Trophy } from 'lucide-react';
import type { ProgramView } from '../../types/list';

interface ProgramItemCardProps {
  team: ProgramView | null | undefined;
}

/**
 * PROGRAM-specific middle content for a compact list row (icon + title + subtitle).
 *
 * Defensive by design: the GET /api/lists/:id/items endpoint hydrates BARE program
 * rows. A program (Team) has no name/org of its own — its label is `level`, and its
 * school is reached via `sport.organization`. For SMART programs the sport/org may be
 * absent entirely, so the subtitle degrades to a muted "—".
 */
export function ProgramItemCard({ team }: ProgramItemCardProps) {
  const sportName = team?.sport?.name?.trim();
  const level = team?.level?.trim();
  // "Football — Varsity" when both present; otherwise whichever exists; never a dangling " — ".
  const title = sportName
    ? level
      ? `${sportName} — ${level}`
      : sportName
    : level || 'Program';

  const org = team?.sport?.organization;
  const location = org ? [org.city, org.state].filter(Boolean).join(', ') : '';
  const subtitle = org ? [org.name, location].filter(Boolean).join(' · ') : '';

  // CRM-115 info line: roster (only when populated and > 0 — over half of Blast
  // teams carry empty rosters, so "0 athletes" everywhere would be noise) · season.
  const infoParts: string[] = [];
  if (team?.rosterCount && team.rosterCount > 0) {
    infoParts.push(`${team.rosterCount} ${team.rosterCount === 1 ? 'athlete' : 'athletes'}`);
  }
  const season = team?.sport?.season;
  if (season) infoParts.push(season.charAt(0) + season.slice(1).toLowerCase());

  return (
    <>
      <div className="flex items-center gap-1.5 min-w-0">
        <Trophy className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
        <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</span>
      </div>
      <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
        {subtitle || '—'}
      </div>
      {infoParts.length > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
          {infoParts.join(' · ')}
        </div>
      )}
    </>
  );
}
